import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authHeaders } from "@/components/AuthContext";
import axios from "axios";
import {
  LogOut, LayoutDashboard, Inbox, RefreshCw,
  Phone, Mail, Building2, MessageCircle, Clock,
  ChevronDown, Filter, Search, Download, FileText, User
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-green-50 text-green-700 border-green-200",
};

const statusLabels = { new: "New", contacted: "Contacted", closed: "Closed" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [checklistLeads, setChecklistLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterService, setFilterService] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [clSearchTerm, setClSearchTerm] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const exportCSV = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterService) params.service = filterService;
      const query = new URLSearchParams(params).toString();
      const url = `${API}/api/inquiries/export${query ? '?' + query : ''}`;
      const res = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `inquiries_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("CSV export failed", err);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterService) params.service = filterService;

      const [inqRes, statsRes, clRes] = await Promise.all([
        axios.get(`${API}/api/inquiries`, { params, headers: authHeaders() }),
        axios.get(`${API}/api/inquiries/stats`, { headers: authHeaders() }),
        axios.get(`${API}/api/checklist-leads`, { headers: authHeaders() }),
      ]);
      setInquiries(inqRes.data);
      setStats(statsRes.data);
      setChecklistLeads(clRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterService, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await axios.patch(`${API}/api/inquiries/${id}`, { status: newStatus }, { headers: authHeaders() });
      fetchData();
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const filtered = inquiries.filter((inq) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return inq.name.toLowerCase().includes(term) ||
      inq.phone.includes(term) ||
      inq.business?.toLowerCase().includes(term) ||
      inq.email?.toLowerCase().includes(term);
  });

  const filteredCL = checklistLeads.filter((lead) => {
    if (!clSearchTerm) return true;
    const term = clSearchTerm.toLowerCase();
    return lead.name.toLowerCase().includes(term) ||
      lead.phone.includes(term) ||
      lead.email?.toLowerCase().includes(term);
  });

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E3E8DC] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/d8a36d0e-acf5-4772-b845-b74bc2191557/images/0a9bc1777adf6f3c4201784effdb03c09d13cbe1b10ecbb42fe5c5571c07a9f7.png"
              alt="Food Safety Partners Logo"
              className="w-7 h-7 object-contain"
            />
            <span className="font-['Outfit'] font-bold text-sm text-[#16231A]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#526B57] hidden sm:inline">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-[#526B57] hover:text-[#D34A4A] transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="stats-grid">
            {[
              { label: "Total Inquiries", value: stats.total, icon: Inbox, color: "text-[#16231A]" },
              { label: "New", value: stats.new, icon: LayoutDashboard, color: "text-blue-600" },
              { label: "Contacted", value: stats.contacted, icon: Phone, color: "text-amber-600" },
              { label: "Closed", value: stats.closed, icon: MessageCircle, color: "text-green-600" },
              { label: "Checklist Downloads", value: checklistLeads.length, icon: FileText, color: "text-[#7CA63A]" },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#E3E8DC] rounded-xl p-5" data-testid={`stat-card-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <s.icon size={16} className={s.color} />
                  <span className="text-[10px] uppercase tracking-wider text-[#526B57] font-bold">{s.label}</span>
                </div>
                <p className={`font-['Outfit'] text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white border border-[#E3E8DC] rounded-xl p-1 w-fit" data-testid="dashboard-tabs">
          <button
            onClick={() => setActiveTab("inquiries")}
            className={`flex items-center gap-1.5 text-xs font-medium px-4 py-2.5 rounded-lg transition-colors ${
              activeTab === "inquiries"
                ? "bg-[#7CA63A] text-white"
                : "text-[#526B57] hover:bg-[#F7F8F5]"
            }`}
            data-testid="tab-inquiries"
          >
            <Inbox size={13} />
            Inquiries ({inquiries.length})
          </button>
          <button
            onClick={() => setActiveTab("checklists")}
            className={`flex items-center gap-1.5 text-xs font-medium px-4 py-2.5 rounded-lg transition-colors ${
              activeTab === "checklists"
                ? "bg-[#7CA63A] text-white"
                : "text-[#526B57] hover:bg-[#F7F8F5]"
            }`}
            data-testid="tab-checklists"
          >
            <FileText size={13} />
            Checklist Downloads ({checklistLeads.length})
          </button>
        </div>

        {/* Inquiries Tab */}
        {activeTab === "inquiries" && (
          <>
            {/* Filters */}
            <div className="bg-white border border-[#E3E8DC] rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="filters-bar">
              <div className="flex items-center gap-2 text-xs text-[#526B57]">
                <Filter size={14} />
                <span className="font-medium">Filters:</span>
              </div>
              <div className="relative">
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  className="text-xs bg-[#F7F8F5] border border-[#E3E8DC] rounded-lg px-3 py-2 pr-8 text-[#16231A] focus:outline-none focus:border-[#7CA63A] appearance-none"
                  data-testid="filter-status-select">
                  <option value="">All Status</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="closed">Closed</option>
                </select>
                <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#526B57] pointer-events-none" />
              </div>
              <div className="relative">
                <select value={filterService} onChange={(e) => setFilterService(e.target.value)}
                  className="text-xs bg-[#F7F8F5] border border-[#E3E8DC] rounded-lg px-3 py-2 pr-8 text-[#16231A] focus:outline-none focus:border-[#7CA63A] appearance-none"
                  data-testid="filter-service-select">
                  <option value="">All Services</option>
                  <option value="Food Safety Audit">Food Safety Audit</option>
                  <option value="Kitchen Hygiene Check">Kitchen Hygiene Check</option>
                  <option value="Compliance Support">Compliance Support</option>
                  <option value="FSSAI License Support">FSSAI License Support</option>
                  <option value="Multiple Services">Multiple Services</option>
                </select>
                <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#526B57] pointer-events-none" />
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, phone, business..."
                  className="w-full pl-9 pr-3 py-2 text-xs bg-[#F7F8F5] border border-[#E3E8DC] rounded-lg text-[#16231A] focus:outline-none focus:border-[#7CA63A]"
                  data-testid="search-input" />
              </div>
              <button onClick={fetchData}
                className="flex items-center gap-1.5 text-xs bg-[#EAF0E2] text-[#2B4131] px-3 py-2 rounded-lg hover:bg-[#DCE6CF] transition-colors"
                data-testid="refresh-btn">
                <RefreshCw size={12} /> Refresh
              </button>
              <button onClick={exportCSV}
                className="flex items-center gap-1.5 text-xs bg-[#7CA63A] text-white px-3 py-2 rounded-lg hover:bg-[#68902A] transition-colors"
                data-testid="export-csv-btn">
                <Download size={12} /> Export CSV
              </button>
            </div>

            {/* Inquiries Table */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-3 border-[#7CA63A] border-t-transparent rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-[#E3E8DC] rounded-xl p-12 text-center" data-testid="no-inquiries">
                <Inbox size={32} className="text-[#E3E8DC] mx-auto mb-3" />
                <p className="text-sm text-[#526B57]">No inquiries found</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E3E8DC] rounded-xl overflow-hidden" data-testid="inquiries-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b border-[#E3E8DC] bg-[#F7F8F5]">
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Name</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Contact</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Service</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Message</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Date</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((inq, idx) => (
                        <tr key={inq.id} className={`border-b border-[#E3E8DC] last:border-0 hover:bg-[#F7F8F5]/50 transition-colors ${idx % 2 === 0 ? '' : 'bg-[#FAFBF8]'}`} data-testid={`inquiry-row-${idx}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#16231A] text-sm">{inq.name}</p>
                            {inq.business && <p className="text-xs text-[#526B57] flex items-center gap-1 mt-0.5"><Building2 size={10} />{inq.business}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-[#16231A] flex items-center gap-1"><Phone size={10} />{inq.phone}</p>
                            {inq.email && <p className="text-xs text-[#526B57] flex items-center gap-1 mt-0.5"><Mail size={10} />{inq.email}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-[#EAF0E2] text-[#2B4131] px-2.5 py-1 rounded-full">{inq.service}</span>
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            <p className="text-xs text-[#526B57] truncate">{inq.message || "—"}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-[#526B57] flex items-center gap-1 whitespace-nowrap"><Clock size={10} />{formatDate(inq.created_at)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative">
                              <select value={inq.status} onChange={(e) => updateStatus(inq.id, e.target.value)}
                                disabled={updatingId === inq.id}
                                className={`text-[11px] font-medium px-2.5 py-1 rounded-full border appearance-none pr-6 cursor-pointer focus:outline-none ${statusColors[inq.status]} ${updatingId === inq.id ? 'opacity-50' : ''}`}
                                data-testid={`status-select-${idx}`}>
                                {Object.entries(statusLabels).map(([k, v]) => (
                                  <option key={k} value={k}>{v}</option>
                                ))}
                              </select>
                              <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Checklist Downloads Tab */}
        {activeTab === "checklists" && (
          <>
            <div className="bg-white border border-[#E3E8DC] rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center" data-testid="cl-filters-bar">
              <div className="flex items-center gap-2 text-xs text-[#526B57]">
                <FileText size={14} />
                <span className="font-medium">Checklist Download Leads</span>
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#526B57]" />
                <input type="text" value={clSearchTerm} onChange={(e) => setClSearchTerm(e.target.value)}
                  placeholder="Search by name, phone, email..."
                  className="w-full pl-9 pr-3 py-2 text-xs bg-[#F7F8F5] border border-[#E3E8DC] rounded-lg text-[#16231A] focus:outline-none focus:border-[#7CA63A]"
                  data-testid="cl-search-input" />
              </div>
              <button onClick={fetchData}
                className="flex items-center gap-1.5 text-xs bg-[#EAF0E2] text-[#2B4131] px-3 py-2 rounded-lg hover:bg-[#DCE6CF] transition-colors"
                data-testid="cl-refresh-btn">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-8 h-8 border-3 border-[#7CA63A] border-t-transparent rounded-full" />
              </div>
            ) : filteredCL.length === 0 ? (
              <div className="bg-white border border-[#E3E8DC] rounded-xl p-12 text-center" data-testid="no-cl-leads">
                <FileText size={32} className="text-[#E3E8DC] mx-auto mb-3" />
                <p className="text-sm text-[#526B57]">No checklist downloads yet</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E3E8DC] rounded-xl overflow-hidden" data-testid="cl-leads-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b border-[#E3E8DC] bg-[#F7F8F5]">
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Name</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Phone</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Email</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Source</th>
                        <th className="px-4 py-3 text-[10px] uppercase tracking-wider font-bold text-[#526B57]">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCL.map((lead, idx) => (
                        <tr key={lead.id} className={`border-b border-[#E3E8DC] last:border-0 hover:bg-[#F7F8F5]/50 transition-colors ${idx % 2 === 0 ? '' : 'bg-[#FAFBF8]'}`} data-testid={`cl-lead-row-${idx}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#16231A] text-sm flex items-center gap-1.5">
                              <User size={12} className="text-[#526B57]" />
                              {lead.name}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-[#16231A] flex items-center gap-1"><Phone size={10} />{lead.phone}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-[#16231A] flex items-center gap-1"><Mail size={10} />{lead.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-[#EAF0E2] text-[#2B4131] px-2.5 py-1 rounded-full flex items-center gap-1 w-fit">
                              <Download size={10} />
                              Checklist
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-[#526B57] flex items-center gap-1 whitespace-nowrap"><Clock size={10} />{formatDate(lead.created_at)}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
